import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Search, Pencil, Trash2, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { listarTurmas, removerTurma, listarAlunos } from "../../api/turmas";
import { getUsuarioLogadoAdaptado as getUsuarioLogado } from "../../utils/ids";
import "./EditarTurmaCoordenador.scss";

const STORAGE_COORDENACAO_ATUAL = "coordenacaoAtualId";

function normStr(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

export default function EditarTurmaCoordenador() {
  const navigate = useNavigate();
  const [busca, setBusca] = useState("");
  const [turmas, setTurmas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [coordenacaoAtualId, setCoordenacaoAtualId] = useState(
    () => localStorage.getItem(STORAGE_COORDENACAO_ATUAL) || null,
  );

  const usuario = useMemo(() => getUsuarioLogado(), []);
  const instituicaoId = usuario?.instituicaoId || usuario?.faculdadeId || null;

  const carregar = useCallback(async () => {
    if (!instituicaoId) {
      setTurmas([]);
      setCarregando(false);
      return;
    }
    setCarregando(true);
    try {
      const lista = await listarTurmas(instituicaoId);
      // Turma N:N: aparece na coordenação atual se ela está entre as vinculadas.
      const filtradas = (Array.isArray(lista) ? lista : []).filter((t) => {
        if (!coordenacaoAtualId) return true;
        const ids = Array.isArray(t.coordenacaoIds) ? t.coordenacaoIds : [];
        return ids.some((id) => String(id) === String(coordenacaoAtualId));
      });
      // Backend já devolve alunosCount em cada TurmaDto. Fallback por turma só
      // se vier ausente (defensivo).
      const enriquecidas = await Promise.all(
        filtradas.map(async (t) => {
          if (typeof t.alunosCount === "number") return t;
          try {
            const alunos = await listarAlunos(t.id);
            return { ...t, alunosCount: Array.isArray(alunos) ? alunos.length : 0 };
          } catch {
            return { ...t, alunosCount: 0 };
          }
        }),
      );
      setTurmas(enriquecidas);
    } catch {
      setTurmas([]);
    } finally {
      setCarregando(false);
    }
  }, [instituicaoId, coordenacaoAtualId]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    const onCoord = () => setCoordenacaoAtualId(
      localStorage.getItem(STORAGE_COORDENACAO_ATUAL) || null,
    );
    const onTurmas = () => carregar();
    window.addEventListener("coordenacaoAtual:changed", onCoord);
    window.addEventListener("turmas:changed", onTurmas);
    return () => {
      window.removeEventListener("coordenacaoAtual:changed", onCoord);
      window.removeEventListener("turmas:changed", onTurmas);
    };
  }, [carregar]);

  const turmasFiltradas = useMemo(() => {
    const termo = normStr(busca);
    if (!termo) return turmas;
    return turmas.filter(
      (t) =>
        normStr(t.nome).includes(termo) ||
        normStr(t.complemento).includes(termo),
    );
  }, [busca, turmas]);

  async function handleDeleteTurma(turma) {
    const result = await Swal.fire({
      title: "Excluir turma?",
      text: `Tem certeza que deseja excluir "${turma.nome}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
      confirmButtonColor: "#d64545",
      cancelButtonColor: "#94a3b8",
    });
    if (!result.isConfirmed) return;

    try {
      await removerTurma(turma.id);
      await Swal.fire({
        icon: "success",
        title: "Turma excluída",
        timer: 1200,
        showConfirmButton: false,
      });
      window.dispatchEvent(new Event("turmas:changed"));
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Erro ao excluir",
        text: e?.message || "Tente novamente.",
      });
    }
  }

  return (
    <div className="editar-turmas-coord">
      <div className="editar-turmas-coord__card">
        <header className="editar-turmas-coord__header">
          <h1>Editar turmas</h1>
          <p>Visualize, edite ou exclua turmas existentes.</p>
        </header>

        <div className="editar-turmas-coord__search">
          <Search size={18} />
          <input
            type="text"
            placeholder="Buscar por nome..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>

        <div className="editar-turmas-coord__list">
          {carregando ? (
            <div className="editar-turmas-coord__empty">Carregando...</div>
          ) : turmasFiltradas.length === 0 ? (
            <div className="editar-turmas-coord__empty">
              Nenhuma turma encontrada.
            </div>
          ) : (
            turmasFiltradas.map((turma) => (
              <article
                key={turma.id}
                className="editar-turmas-coord__item"
                style={{ "--barColor": turma.cor || "#60a5fa" }}
              >
                <div className="editar-turmas-coord__bar" />
                <div className="editar-turmas-coord__content">
                  <div className="editar-turmas-coord__main">
                    <h2 className="editar-turmas-coord__title">
                      {turma.complemento
                        ? `${turma.nome} - ${turma.complemento}`
                        : turma.nome}
                    </h2>
                  </div>
                  <div className="editar-turmas-coord__right">
                    <div className="editar-turmas-coord__students">
                      <Users size={17} />
                      <span>{turma.alunosCount ?? 0} alunos</span>
                    </div>
                    <div className="editar-turmas-coord__actions">
                      <button
                        type="button"
                        onClick={() => navigate(`/editar-turma/${turma.id}`)}
                        aria-label="Editar turma"
                        title="Editar turma"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteTurma(turma)}
                        aria-label="Excluir turma"
                        title="Excluir turma"
                        className="is-danger"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
